import { useMemo } from "react";
import IntranetLayout from "@/components/IntranetLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Star, Users, Ship, TrendingUp, MessageSquare } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const EmployeeSatisfaction = () => {
  const { data: surveys = [], isLoading } = useQuery({
    queryKey: ["satisfaction-surveys-intranet"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("satisfaction_surveys" as any)
        .select("*")
        .order("submitted_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });

  const overallScore = useMemo(() => {
    if (!surveys.length) return 0;
    return Math.round((surveys.reduce((s: number, r: any) => s + (r.rating_overall || 0), 0) / surveys.length) * 10) / 10;
  }, [surveys]);

  const npsAvg = useMemo(() => {
    const withNps = surveys.filter((s: any) => s.nps_score !== null);
    if (!withNps.length) return 0;
    return Math.round((withNps.reduce((a: number, s: any) => a + s.nps_score, 0) / withNps.length) * 10) / 10;
  }, [surveys]);

  const vesselScores = useMemo(() => {
    const map: Record<string, { total: number; count: number }> = {};
    surveys.forEach((s: any) => {
      if (s.vessel_name) {
        if (!map[s.vessel_name]) map[s.vessel_name] = { total: 0, count: 0 };
        map[s.vessel_name].total += s.rating_overall || 0;
        map[s.vessel_name].count += 1;
      }
    });
    return Object.entries(map).map(([vessel, d]) => ({
      vessel,
      score: Math.round((d.total / d.count) * 10) / 10,
      count: d.count,
    }));
  }, [surveys]);

  // Captain performance
  const captainScores = useMemo(() => {
    const map: Record<string, { total: number; count: number; vessel: string }> = {};
    surveys.forEach((s: any) => {
      if (s.captain_name) {
        if (!map[s.captain_name]) map[s.captain_name] = { total: 0, count: 0, vessel: s.vessel_name || "" };
        map[s.captain_name].total += s.rating_overall || 0;
        map[s.captain_name].count += 1;
      }
    });
    return Object.entries(map).map(([captain, d]) => ({
      captain,
      score: Math.round((d.total / d.count) * 10) / 10,
      count: d.count,
      vessel: d.vessel,
    })).sort((a, b) => b.score - a.score);
  }, [surveys]);

  const trends = useMemo(() => {
    const monthMap: Record<string, number[]> = {};
    surveys.forEach((s: any) => {
      const d = new Date(s.submitted_at);
      const key = d.toLocaleString("en", { month: "short" });
      if (!monthMap[key]) monthMap[key] = [];
      if (s.rating_overall) monthMap[key].push(s.rating_overall);
    });
    return Object.entries(monthMap).map(([month, scores]) => ({
      month,
      score: Math.round((scores.reduce((a, b) => a + b, 0) / scores.length) * 10) / 10,
    }));
  }, [surveys]);

  if (isLoading) {
    return (
      <IntranetLayout>
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
        </div>
      </IntranetLayout>
    );
  }

  return (
    <IntranetLayout>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Client Satisfaction Analytics</h1>
        <p className="text-sm text-muted-foreground">Live survey results from passengers — includes crew and captain data</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Card>
          <CardContent className="p-4 text-center">
            <Star className="w-6 h-6 text-amber-400 fill-amber-400 mx-auto mb-1" />
            <p className="text-3xl font-bold">{overallScore}</p>
            <p className="text-xs text-muted-foreground">Overall Score / 5</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <TrendingUp className="w-6 h-6 text-blue-500 mx-auto mb-1" />
            <p className="text-3xl font-bold">{npsAvg}</p>
            <p className="text-xs text-muted-foreground">Avg NPS / 10</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <MessageSquare className="w-6 h-6 text-green-500 mx-auto mb-1" />
            <p className="text-3xl font-bold">{surveys.length}</p>
            <p className="text-xs text-muted-foreground">Total Surveys</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="w-6 h-6 text-purple-500 mx-auto mb-1" />
            <p className="text-3xl font-bold">{captainScores.length}</p>
            <p className="text-xs text-muted-foreground">Captains Rated</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="captain">Captain & Crew</TabsTrigger>
          <TabsTrigger value="surveys">All Surveys</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Score Trend</CardTitle>
              </CardHeader>
              <CardContent>
                {trends.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <LineChart data={trends}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                      <YAxis domain={[3, 5]} tick={{ fontSize: 10 }} />
                      <Tooltip />
                      <Line type="monotone" dataKey="score" stroke="#0066CC" strokeWidth={2.5} dot={{ r: 4 }} name="Avg Score" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">No data yet</p>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Per-Vessel Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                {vesselScores.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={vesselScores} layout="vertical">
                      <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 10 }} />
                      <YAxis type="category" dataKey="vessel" tick={{ fontSize: 10 }} width={100} />
                      <Tooltip />
                      <Bar dataKey="score" fill="#0066CC" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-8">No data</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="captain">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Captain Performance Table */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Captain Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {captainScores.map((c, i) => (
                    <div key={c.captain} className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
                      <span className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white ${
                        i === 0 ? "bg-amber-500" : i === 1 ? "bg-gray-400" : "bg-orange-600"
                      }`}>{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{c.captain}</p>
                        <p className="text-[10px] text-muted-foreground">{c.vessel} · {c.count} surveys</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-amber-400 fill-amber-400" />
                        <span className="text-sm font-bold">{c.score}</span>
                      </div>
                    </div>
                  ))}
                  {captainScores.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-4">No captain data yet</p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Crew mentioned in recent surveys */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Crew on Shift (Recent Surveys)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[350px] overflow-y-auto">
                  {surveys.slice(0, 8).map((s: any) => {
                    let crew: string[] = [];
                    try { crew = typeof s.crew_on_shift === "string" ? JSON.parse(s.crew_on_shift) : (s.crew_on_shift || []); } catch { crew = []; }
                    return (
                      <div key={s.id} className="p-3 rounded-lg bg-muted/50">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-bold text-blue-600">{s.voyage_ref}</span>
                          <div className="flex items-center gap-1">
                            <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
                            <span className="text-xs font-bold">{s.rating_overall}/5</span>
                          </div>
                        </div>
                        <p className="text-[11px] text-muted-foreground mb-1">
                          <Ship className="w-3 h-3 inline mr-1" />{s.vessel_name} · {s.captain_name || "Unknown Captain"}
                        </p>
                        {crew.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {crew.map((c: string, ci: number) => (
                              <span key={ci} className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">{c}</span>
                            ))}
                          </div>
                        )}
                        {s.comments && <p className="text-xs text-foreground mt-1.5 italic">"{s.comments}"</p>}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="surveys">
          <Card>
            <CardContent className="p-4">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 px-2 text-xs font-medium text-muted-foreground">Voyage</th>
                      <th className="text-left py-2 px-2 text-xs font-medium text-muted-foreground">Vessel</th>
                      <th className="text-left py-2 px-2 text-xs font-medium text-muted-foreground">Client</th>
                      <th className="text-left py-2 px-2 text-xs font-medium text-muted-foreground">Captain</th>
                      <th className="text-center py-2 px-2 text-xs font-medium text-muted-foreground">Overall</th>
                      <th className="text-center py-2 px-2 text-xs font-medium text-muted-foreground">Crew</th>
                      <th className="text-center py-2 px-2 text-xs font-medium text-muted-foreground">Vessel</th>
                      <th className="text-center py-2 px-2 text-xs font-medium text-muted-foreground">Punct.</th>
                      <th className="text-center py-2 px-2 text-xs font-medium text-muted-foreground">Comms</th>
                      <th className="text-center py-2 px-2 text-xs font-medium text-muted-foreground">Safety</th>
                      <th className="text-center py-2 px-2 text-xs font-medium text-muted-foreground">NPS</th>
                      <th className="text-left py-2 px-2 text-xs font-medium text-muted-foreground">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {surveys.map((s: any) => (
                      <tr key={s.id} className="border-b last:border-0 hover:bg-muted/30">
                        <td className="py-2 px-2 font-medium text-blue-600">{s.voyage_ref}</td>
                        <td className="py-2 px-2">{s.vessel_name || "—"}</td>
                        <td className="py-2 px-2">{s.client_name || "—"}</td>
                        <td className="py-2 px-2 text-xs">{s.captain_name || "—"}</td>
                        <td className="py-2 px-2 text-center font-bold">{s.rating_overall || "—"}</td>
                        <td className="py-2 px-2 text-center">{s.rating_crew || "—"}</td>
                        <td className="py-2 px-2 text-center">{s.rating_vessel || "—"}</td>
                        <td className="py-2 px-2 text-center">{s.rating_punctuality || "—"}</td>
                        <td className="py-2 px-2 text-center">{s.rating_communication || "—"}</td>
                        <td className="py-2 px-2 text-center">{s.rating_safety || "—"}</td>
                        <td className="py-2 px-2 text-center">{s.nps_score ?? "—"}</td>
                        <td className="py-2 px-2 text-xs text-muted-foreground">{new Date(s.submitted_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {surveys.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-8">No surveys submitted yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </IntranetLayout>
  );
};

export default EmployeeSatisfaction;
